import { useMemo } from 'react';

import { MediaItem } from '../../../app/services/mediaItems';
import { TimelineTrackData } from '../components/TimelinePanel';
import {
  useMultiWaveformPeaks,
  WaveformPeaksResult,
  WaveformRequest,
} from '../hooks/useWaveformPeaks';
import { MaSegment, dbToGain, maSegmentDisplaySeconds } from './maTypes';

/**
 * MA セグメントからマルチトラックタイムラインの描画データを組み立てるフック。
 *
 * トラック構成（上から）:
 * - MOV: 動画内オーディオ（動画の波形を offset 分ずらして 0 起点で表示）
 * - A1, A2, ...: シーケンスのオーディオクリップ（トラック番号順）。
 *   トラック名指定（RPP の Render Track List 等）があれば名前を表示
 *
 * 波形は Lambda 事前計算 JSON（waveform_url）を優先し、
 * なければ original_url のブラウザデコードにフォールバックする。
 */

const PENDING_RESULT: WaveformPeaksResult = { peaks: null, isLoading: true, error: false };

export function useMaTimelineTracks(
  segment: MaSegment | null,
  enabled: boolean,
  /** `_a`/`_b` バリアントを持つアイテム ID（波形をブルー表示にする対象） */
  variantItemIds?: Set<string>,
  /** 動画トラックのラベル（RPP のビデオトラック名。未指定・空は 'MOV'） */
  videoTrackName?: string | null,
): TimelineTrackData[] {
  const requests = useMemo<WaveformRequest[]>(() => {
    if (!segment || !enabled) return [];
    const reqs: WaveformRequest[] = [];
    const seen = new Set<string>();
    const push = (item: MediaItem) => {
      if (seen.has(item.id)) return;
      seen.add(item.id);
      reqs.push({
        key: item.id,
        waveformUrl: item.has_waveform && item.waveform_url ? item.waveform_url : null,
        fallbackSrc: item.original_url,
      });
    };
    segment.videoSpans.forEach(span => push(span.videoItem));
    segment.clips.forEach(clip => push(clip.audioItem));
    return reqs;
  }, [segment, enabled]);

  const results = useMultiWaveformPeaks({ requests, enabled: enabled && !!segment });

  return useMemo<TimelineTrackData[]>(() => {
    if (!segment || !enabled) return [];

    // MOV トラックは動画表示区間（videoSpans）ごとにクリップを並べる
    //（オプション候補の動画はスロット位置から先の区間として表示される）
    const videoTrack: TimelineTrackData = {
      label: videoTrackName?.trim() || 'MOV',
      clips: segment.videoSpans.map((span, i) => {
        const result = results[span.videoItem.id] ?? PENDING_RESULT;
        const next = segment.videoSpans[i + 1];
        // 区間の表示尺 = 次区間の開始まで（最終区間は実効表示尺（候補による延長
        // 込み）まで。未指定なら実尺に任せる）
        const displaySeconds = maSegmentDisplaySeconds(segment);
        const durationSeconds = next
          ? Math.max(0, next.startSeconds - span.startSeconds)
          : displaySeconds != null
            ? Math.max(0, displaySeconds - span.startSeconds)
            : null;
        return {
          key: `video-${i}-${span.videoItem.id}`,
          startSeconds: span.startSeconds,
          durationSeconds,
          peaks: result.peaks,
          isLoading: result.isLoading,
          error: result.error,
          peaksOffsetSeconds: span.fileOffsetSeconds,
          peaksDurationSeconds: span.videoItem.duration ?? null,
          label: span.videoItem.file_name,
          // 動画内蔵オーディオは gray
          tone: 'video' as const,
        };
      }),
    };

    const trackNumbers = Array.from(new Set(segment.clips.map(c => c.track))).sort(
      (a, b) => a - b,
    );
    const audioTracks: TimelineTrackData[] = trackNumbers.map(trackNo => {
      const trackClips = segment.clips.filter(c => c.track === trackNo);
      const trackName = trackClips.find(c => c.trackName != null)?.trackName ?? null;
      return {
        label: trackName ?? `A${trackNo}`,
        trackNo,
        clips: trackClips.map(clip => {
          const result = results[clip.audioItem.id] ?? PENDING_RESULT;
          return {
            key: clip.editId ?? `clip-${clip.line}-${clip.audioItem.id}`,
            editId: clip.editId,
            optionSlotKey: clip.optionSlotKey ?? null,
            startSeconds: clip.startSeconds,
            durationSeconds: clip.durationSeconds ?? clip.audioItem.duration ?? null,
            // 同一トラック後着優先カット位置（クリップ相対の上限尺に変換）
            maxDurationSeconds:
              clip.truncateAtSeconds != null
                ? Math.max(0, clip.truncateAtSeconds - clip.startSeconds)
                : null,
            peaks: result.peaks,
            isLoading: result.isLoading,
            error: result.error,
            peaksOffsetSeconds: 0,
            peaksDurationSeconds: clip.audioItem.duration ?? null,
            label: clip.audioItem.file_name,
            // クリップゲインを波形振幅に反映（-6dB で半分）
            amplitudeScale: dbToGain(clip.gainDb),
            // `_a`/`_b` オプションあり = blue / なし = light-gray
            tone: variantItemIds?.has(clip.audioItem.id) ? 'variant' : 'plain',
            // RPP のリージョン / マーカー個別色（またはレーン色フォールバック）。
            // 指定があればトーンより優先して描画される
            colorHex: clip.color ?? null,
          };
        }),
      };
    });

    return [videoTrack, ...audioTracks];
  }, [segment, enabled, results, variantItemIds, videoTrackName]);
}
